import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import DocumentScoringDimensionPeriodListComponent from './DocumentScoringDimensionPeriodListComponent';
import * as editableDocument from 'utils/model/editableDocument';
import { collection } from 'utils/immutable';
import { getRandomInt } from 'utils/functions';
import flow from 'lodash.flow';
import { isMandatory, isInRange } from 'utils/simple-validations';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { showInfo } from 'actions/actionCreators/snackbarActions';

const getPeriodDimensionItemValidationRules = () => ({
  dimensionPeriodYear: document => (document.selected ? [isMandatory(), isInRange(1700, 4000)] : []),
  dimensionPeriodMonth: document => (document.selected ? [isMandatory(), isInRange(1, 12)] : [])
});

const getPeriodDimensionBusinessRules = dimensions => ({
  dimensionPeriodName: document => (document.dimensionPeriodYear || '') + '.' + (document.dimensionPeriodMonth || ''),
  dimensionPeriodCode: document => (document.dimensionPeriodYear || '') + (document.dimensionPeriodMonth || ''),
  dimensionName: document => {
    var dimensionToUpdate = dimensions.find(x => x.id === document.dimensionId);
    return dimensionToUpdate && dimensionToUpdate.name;
  }
});

const getPeriodDimensionsValidationInfo = (localPeriods, t) => {
  let selectedPeriodDimensions = localPeriods.filter(x => x.document.selected);

  if (selectedPeriodDimensions.length > 0) {
    selectedPeriodDimensions = selectedPeriodDimensions.map(editableDocument.validate(getPeriodDimensionItemValidationRules(), false));

    if (selectedPeriodDimensions.length > 0 && selectedPeriodDimensions.some(x => !x.validationInfo.isValid)) {
      return { isValid: false, message: t('DocumentEdit.Scoring.ScoringIndicator.Dimensions.YearMonthError') };
    }

    var dimensionsIds = selectedPeriodDimensions
      .filter(x => x.document.dimensionId !== null)
      .map(function (item) {
        return item.document.dimensionId;
      });
    var dimensionsDuplicated = dimensionsIds.some(function (item, idx) {
      return dimensionsIds.indexOf(item) !== idx;
    });

    if (dimensionsDuplicated) {
      return { isValid: false, message: t('DocumentEdit.Scoring.ScoringIndicator.Dimensions.PeriodsDuplicated') };
    }

    var dimensionPeriodCodes = selectedPeriodDimensions.map(function (item) {
      return item.document.dimensionPeriodCode;
    });
    var dimensionPeriodCodesDuplicated = dimensionPeriodCodes.some(function (item, idx) {
      return dimensionPeriodCodes.indexOf(item) !== idx;
    });

    if (dimensionPeriodCodesDuplicated) {
      return { isValid: false, message: t('DocumentEdit.Scoring.ScoringIndicator.Dimensions.YearMonthDuplicated') };
    }
  }

  return { isValid: true, message: null };
};

const DocumentScoringDimensionPeriodListContainer = ({
  t,
  showModal,
  savingModal,
  actions,
  onSavePeriodDimensions,
  dimensions,
  periods,
  documentScoringGridId,
  onCloseDimensionListModal
}) => {
  const [state, setState] = useState({
    localPeriods: [],
    prevLocalPeriods: []
  });

  useEffect(() => {
    setState({
      localPeriods: periods.map(editableDocument.create),
      prevLocalPeriods: periods.map(editableDocument.create)
    });
  }, [periods]);

  const onPeriodDimensionChange = idx => (prop, value) => {
    let updateAndValidate = flow(
      editableDocument.update(prop, value, getPeriodDimensionBusinessRules(dimensions)),
      editableDocument.validate(getPeriodDimensionItemValidationRules(), false)
    );

    setState({
      ...state,
      localPeriods: collection.update(idx, updateAndValidate, state.localPeriods)
    });
  };

  const onSaveLocalPeriodDimensions = () => {
    const { isValid, message } = getPeriodDimensionsValidationInfo(state.localPeriods, t);

    if (!isValid) {
      actions.showInfo(message);
      return;
    }

    let selectedPeriodDimensions = state.localPeriods;

    if (selectedPeriodDimensions.length > 0) {
      selectedPeriodDimensions.forEach(flow(editableDocument.resetDirtyInfo, editableDocument.resetValidationInfo));
    }

    setState({
      ...state,
      localPeriods: selectedPeriodDimensions,
      prevLocalPeriods: selectedPeriodDimensions
    });

    onSavePeriodDimensions(state.localPeriods.map(x => x.document));
  };

  const onCloseLocalPeriodDimensions = () => {
    setState({
      ...state,
      localPeriods: state.prevLocalPeriods
    });

    onCloseDimensionListModal();
  };

  const onAddNewPeriodDimension = () => {
    const newPeriodDimension = flow(
      editableDocument.create,
      editableDocument.validate(getPeriodDimensionItemValidationRules(), false)
    )({
      dimensionId: null,
      dimensionName: null,
      dimensionPeriodId: getRandomInt(10000, 2147483647),
      dimensionPeriodMonth: null,
      dimensionPeriodName: null,
      dimensionPeriodSourceId: -1,
      dimensionPeriodSourceName: 'Manual',
      dimensionPeriodYear: null,
      documentScoringFinancialIndicatorPeriodDimensionId: getRandomInt(10000, 2147483647),
      documentScoringGridId: documentScoringGridId,
      selected: true,
      isReadOnly: false
    });

    setState({
      ...state,
      localPeriods: collection.insert(state.localPeriods.length, newPeriodDimension, state.localPeriods)
    });
  };

  return (
    <DocumentScoringDimensionPeriodListComponent
      showModal={showModal}
      savingModal={savingModal}
      dimensions={dimensions}
      onCloseLocalDimensionListModal={onCloseLocalPeriodDimensions}
      onSaveLocalPeriodDimensions={onSaveLocalPeriodDimensions}
      onAddNewPeriodDimension={onAddNewPeriodDimension}
      onPeriodDimensionChange={onPeriodDimensionChange}
      periods={state.localPeriods || []}
    />
  );
};

DocumentScoringDimensionPeriodListContainer.propTypes = {
  showModal: PropTypes.bool.isRequired,
  savingModal: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  onCloseDimensionListModal: PropTypes.func.isRequired,
  dimensions: PropTypes.array,
  periods: PropTypes.array.isRequired,
  documentScoringGridId: PropTypes.number.isRequired,
  onSavePeriodDimensions: PropTypes.func.isRequired,
  actions: PropTypes.object.isRequired
};

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ showInfo }, dispatch)
  };
}

export default compose(connect(null, mapDispatchToProps), withTranslation())(DocumentScoringDimensionPeriodListContainer);
